import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Minus, Plus, Trash2, X } from "./icons";
import { useRFQ } from "../context/RFQContext";
import { useAuth } from "../context/AuthContext";
import { getProductDisplayText } from "../lib/productDisplay";
import { useLanguage } from "../context/LanguageContext";

type Step = "cart" | "details";

export default function RFQDrawer() {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const { items, isDrawerOpen, closeDrawer, removeItem, setQuantity, clearItems } = useRFQ();
  const [step, setStep] = useState<Step>("cart");
  const [notes, setNotes] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestCompany, setGuestCompany] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDrawer();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeDrawer]);

  useEffect(() => {
    document.body.style.overflow = isDrawerOpen ? "hidden" : "";
  }, [isDrawerOpen]);

  useEffect(() => {
    if (isDrawerOpen) setStep("cart");
  }, [isDrawerOpen]);

  const knownTotal = items.reduce((sum, item) => sum + (item.price ?? 0) * item.quantity, 0);
  const hasUnknownPrice = items.some((item) => item.price === undefined);
  const totalLabel = hasUnknownPrice
    ? knownTotal > 0
      ? `${t("pricePrefixFrom")}${knownTotal.toLocaleString("ru-RU")} ₸ + ${t("rfqPriceOnRequestShort").toLowerCase()}`
      : t("priceOnRequest")
    : `${knownTotal.toLocaleString("ru-RU")} ₸`;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError("");

    setIsSubmitting(true);
    try {
      const res = await fetch("/api/rfq", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          notes,
          ...(user
            ? {}
            : { name: guestName, company: guestCompany, phone: guestPhone, email: guestEmail }),
          items: (items || []).map((item) => ({
            name: item.name,
            quantity: item.quantity,
            price: item.price,
          })),
        }),
      });
      if (res.status === 401) {
        // Stale client-side auth state: the page loaded as "logged in" but the
        // session cookie has since expired or been invalidated server-side.
        // Resubmitting would 401 forever, so drop back to the guest-checkout
        // view (via logout()'s setUser(null)) instead of a dead-end retry loop.
        // The message stays visible through that state change so the form
        // reappearing with empty contact fields doesn't read as the button
        // having silently done nothing.
        setSubmitError(t("rfqSessionExpiredError"));
        await logout();
        return;
      }
      if (!res.ok) {
        // Surface the specific reason where one exists instead of a blanket
        // "try again" — an account missing required contact info (e.g. an
        // older account created before phone/company were mandatory) will
        // fail on every retry, and looked identical to a transient network
        // error until this was split out (see the report this fixed:
        // resubmitting a real request kept failing with no clue why).
        const body = (await res.json().catch(() => null)) as { code?: string } | null;
        setSubmitError(
          body?.code === "ACCOUNT_MISSING_CONTACT_INFO" ? t("rfqAccountMissingInfoError") : t("rfqFormError")
        );
        return;
      }
      setSubmitted(true);
      setNotes("");
      setGuestName("");
      setGuestCompany("");
      setGuestPhone("");
      setGuestEmail("");
      clearItems();
    } catch {
      setSubmitError(t("rfqFormError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div
        onClick={closeDrawer}
        className={`fixed inset-0 z-[60] bg-ink/60 transition-opacity duration-300 ease-engineered ${
          isDrawerOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
      />

      <aside
        aria-hidden={!isDrawerOpen}
        className={`fixed inset-y-0 right-0 z-[70] flex w-full max-w-md flex-col border-l border-steel-200 bg-white transition-transform duration-300 ease-engineered ${
          isDrawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-steel-200 px-6 py-5">
          <h2 className="font-sans text-sm font-semibold text-ink">
            {step === "cart" ? `${t("rfqInProjectPrefix")} (${items.length})` : t("rfqYourDetails")}
          </h2>
          <button
            type="button"
            aria-label={t("rfqCloseAria")}
            onClick={closeDrawer}
            className="flex h-9 w-9 items-center justify-center text-ink transition-colors duration-150 ease-engineered hover:bg-steel-900/5 hover:text-accent active:bg-steel-900/5"
          >
            <X size={20} />
          </button>
        </div>

        {submitted ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
            <CheckCircle2 size={40} className="text-accent" />
            <h3 className="font-sans text-xl font-semibold tracking-tight text-ink">
              {t("rfqSuccessTitle")}
            </h3>
            <p className="text-sm leading-relaxed text-steel-600">
              {t("rfqSuccessDesc")}
            </p>
            <button
              type="button"
              onClick={() => {
                setSubmitted(false);
                closeDrawer();
              }}
              className="btn-dark-outline mt-2"
            >
              {t("rfqClose")}
            </button>
          </div>
        ) : step === "cart" ? (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {items.length === 0 ? (
                <p className="py-16 text-center text-sm text-steel-500">
                  {t("rfqEmptyCart")}
                </p>
              ) : (
                <ul className="flex flex-col divide-y divide-steel-100">
                  {(items || []).map((item) => {
                    const { cleanedName, cleanedModel, showModel } = getProductDisplayText(
                      item.name,
                      item.model,
                      item.brand
                    );
                    return (
                    <li key={item.id} className="flex gap-3 py-3">
                      <img
                        src={item.image}
                        alt={item.name}
                        draggable={false}
                        loading="lazy"
                        className="h-14 w-14 shrink-0 self-center border border-steel-200 bg-white object-cover"
                      />
                      <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-ink">{cleanedName}</p>
                            {showModel && (
                              <p className="truncate text-xs uppercase tracking-wide2 text-steel-500">
                                {cleanedModel}
                              </p>
                            )}
                          </div>
                          <p className="shrink-0 text-sm font-semibold text-ink">
                            {item.price === undefined
                              ? t("rfqPriceOnRequestShort")
                              : `${(item.price * item.quantity).toLocaleString("ru-RU")} ₸`}
                          </p>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="inline-flex items-center border border-steel-300">
                            <button
                              type="button"
                              aria-label={`${t("rfqDecreaseQtyAria")}: ${item.name}`}
                              onClick={() => setQuantity(item.id, item.quantity - 1)}
                              className="flex h-6 w-6 items-center justify-center text-ink transition-colors duration-200 ease-engineered hover:text-accent"
                            >
                              <Minus size={11} />
                            </button>
                            <span className="w-6 text-center text-xs text-ink">
                              {item.quantity}
                            </span>
                            <button
                              type="button"
                              aria-label={`${t("rfqIncreaseQtyAria")}: ${item.name}`}
                              onClick={() => setQuantity(item.id, item.quantity + 1)}
                              className="flex h-6 w-6 items-center justify-center text-ink transition-colors duration-200 ease-engineered hover:text-accent"
                            >
                              <Plus size={11} />
                            </button>
                          </div>
                          <button
                            type="button"
                            aria-label={`${t("rfqRemoveAria")} ${item.name}`}
                            onClick={() => removeItem(item.id)}
                            className="flex h-8 w-8 items-center justify-center text-steel-400 transition-colors duration-200 ease-engineered hover:text-accent"
                          >
                            <Trash2 size={19} />
                          </button>
                        </div>
                      </div>
                    </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="flex flex-col gap-3 border-t border-steel-200 px-6 py-4">
              {items.length > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-steel-600">{t("rfqTotal")}</span>
                  <span className="text-lg font-semibold text-ink">{totalLabel}</span>
                </div>
              )}
              <button
                type="button"
                disabled={items.length === 0}
                onClick={() => setStep("details")}
                className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t("rfqContinue")}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <button
                type="button"
                onClick={() => setStep("cart")}
                className="mb-4 text-sm font-semibold text-steel-500 hover:text-ink"
              >
                ← {t("rfqBackToListText")}
              </button>
              <div className="flex items-center justify-between border border-steel-200 px-4 py-3 text-sm">
                <span className="text-steel-600">
                  {items.length} {items.length === 1 ? t("rfqItemSingular") : t("rfqItemPlural")}
                </span>
                <span className="font-semibold text-ink">{totalLabel}</span>
              </div>

              <form id="rfq-details-form" onSubmit={handleSubmit} noValidate className="mt-6 flex flex-col gap-3">
                {!user && (
                  <>
                    <label className="flex flex-col gap-1">
                      <span className="text-sm font-semibold text-steel-500">
                        {t("rfqFormName")}
                      </span>
                      <input
                        type="text"
                        required
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        className="border border-steel-300 px-3 py-1.5 text-sm text-ink outline-none focus:border-ink"
                      />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-sm font-semibold text-steel-500">
                        {t("rfqFormCompany")}
                      </span>
                      <input
                        type="text"
                        required
                        value={guestCompany}
                        onChange={(e) => setGuestCompany(e.target.value)}
                        className="border border-steel-300 px-3 py-1.5 text-sm text-ink outline-none focus:border-ink"
                      />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-sm font-semibold text-steel-500">
                        {t("rfqFormPhone")}
                      </span>
                      <input
                        type="tel"
                        required
                        value={guestPhone}
                        onChange={(e) => setGuestPhone(e.target.value)}
                        className="border border-steel-300 px-3 py-1.5 text-sm text-ink outline-none focus:border-ink"
                      />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-sm font-semibold text-steel-500">
                        {t("rfqFormEmail")}
                      </span>
                      <input
                        type="email"
                        required
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        className="border border-steel-300 px-3 py-1.5 text-sm text-ink outline-none focus:border-ink"
                      />
                    </label>
                  </>
                )}
                <label className="flex flex-1 flex-col gap-1">
                  <span className="text-sm font-semibold text-steel-500">
                    {t("rfqFormNotes")}
                  </span>
                  <textarea
                    rows={user ? 6 : 3}
                    value={notes}
                    placeholder="Укажите пожелания по срокам, монтажу или доставке..."
                    onChange={(e) => setNotes(e.target.value)}
                    className="min-h-24 flex-1 resize-none border border-steel-300 px-3 py-1.5 text-sm text-ink placeholder-steel-400 outline-none focus:border-ink"
                  />
                </label>
                {!user && (
                  <p className="text-xs text-steel-500">
                    {t("rfqGuestLoginHint")}{" "}
                    <Link to="/login" onClick={closeDrawer} className="font-semibold text-ink underline">
                      {t("rfqGuestLoginCta")}
                    </Link>
                  </p>
                )}
                {submitError && <p className="text-sm text-accent">{submitError}</p>}
              </form>
            </div>

            <div className="border-t border-steel-200 px-6 py-4">
              <button
                type="submit"
                form="rfq-details-form"
                disabled={isSubmitting}
                className="btn-primary w-full disabled:opacity-60"
              >
                {isSubmitting ? "…" : t("rfqSubmit")}
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
